/**
 * Sport.blitz.bg - Main JavaScript
 */

(function() {
    'use strict';

    // =========================================================================
    // Theme Switcher
    // =========================================================================
    const ThemeSwitcher = {
        COOKIE_NAME: 'theme',
        COOKIE_DAYS: 365,
        DEFAULT_THEME: 'dark',

        init() {
            const savedTheme = this.getCookie(this.COOKIE_NAME) || this.DEFAULT_THEME;
            this.setTheme(savedTheme);
            this.bindEvents();
        },

        getCookie(name) {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
            return null;
        },

        setCookie(name, value, days) {
            const expires = new Date(Date.now() + days * 864e5).toUTCString();
            document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
        },

        setTheme(theme) {
            document.documentElement.setAttribute('data-theme', theme);
            this.setCookie(this.COOKIE_NAME, theme, this.COOKIE_DAYS);

            document.querySelectorAll('[data-theme-set]').forEach(btn => {
                btn.setAttribute('aria-pressed', btn.dataset.themeSet === theme ? 'true' : 'false');
            });
        },

        bindEvents() {
            document.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-theme-set]');
                if (btn) {
                    this.setTheme(btn.dataset.themeSet);
                }
            });
        }
    };

    // =========================================================================
    // Dialog Manager (for drawer and search)
    // =========================================================================
    const DialogManager = {
        init() {
            this.bindEvents();
        },

        lockScroll() {
            document.body.classList.add('is-locked');
        },

        unlockScroll() {
            document.body.classList.remove('is-locked');
        },

        bindEvents() {
            const self = this;

            // Open dialog
            document.addEventListener('click', (e) => {
                const openBtn = e.target.closest('[data-open]');
                if (openBtn) {
                    const dialogId = openBtn.dataset.open;
                    const dialog = document.getElementById(dialogId);
                    if (dialog && typeof dialog.showModal === 'function') {
                        dialog.showModal();
                        openBtn.setAttribute('aria-expanded', 'true');
                        self.lockScroll();

                        // Focus first focusable element
                        const focusable = dialog.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                        if (focusable) focusable.focus();
                    }
                }

                // Close dialog
                const closeBtn = e.target.closest('[data-close]');
                if (closeBtn) {
                    const dialog = closeBtn.closest('dialog');
                    if (dialog && typeof dialog.close === 'function') {
                        dialog.close();
                        self.unlockScroll();
                        document.querySelectorAll(`[data-open="${dialog.id}"]`).forEach(btn => {
                            btn.setAttribute('aria-expanded', 'false');
                        });
                    }
                }
            });

            // Close on backdrop click
            document.querySelectorAll('dialog').forEach(dialog => {
                dialog.addEventListener('click', (e) => {
                    if (e.target === dialog) {
                        dialog.close();
                        self.unlockScroll();
                        document.querySelectorAll(`[data-open="${dialog.id}"]`).forEach(btn => {
                            btn.setAttribute('aria-expanded', 'false');
                        });
                    }
                });
            });

            // Close on Escape
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    const openDialog = document.querySelector('dialog[open]');
                    if (openDialog) {
                        openDialog.close();
                        self.unlockScroll();
                        document.querySelectorAll(`[data-open="${openDialog.id}"]`).forEach(btn => {
                            btn.setAttribute('aria-expanded', 'false');
                        });
                    }
                }
            });
        }
    };

    // =========================================================================
    // Sticky Header with IntersectionObserver
    // =========================================================================
    const StickyHeader = {
        header: null,
        catnav: null,
        sentinel: null,

        init() {
            this.header = document.querySelector('.site-header');
            this.catnav = document.querySelector('.catnav');
            this.sentinel = document.querySelector('.catnav-sentinel');

            if (this.header && this.sentinel) {
                this.setupObserver();
            }
        },

        setupObserver() {
            // Observer for header stuck state
            const headerObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach(entry => {
                        // When sentinel is not visible (scrolled past), header is stuck
                        if (!entry.isIntersecting) {
                            this.header.classList.add('is-stuck');
                            if (this.catnav) this.catnav.classList.add('is-stuck');
                        } else {
                            this.header.classList.remove('is-stuck');
                            if (this.catnav) this.catnav.classList.remove('is-stuck');
                        }
                    });
                },
                {
                    threshold: 0,
                    rootMargin: '0px'
                }
            );

            headerObserver.observe(this.sentinel);
        }
    };

    // =========================================================================
    // Category Navigation Overflow Detection
    // =========================================================================
    const CatnavOverflow = {
        init() {
            const list = document.querySelector('.catnav__list');
            if (!list) return;

            this.checkOverflow(list);

            // Re-check on resize
            window.addEventListener('resize', () => {
                this.checkOverflow(list);
            });
        },

        checkOverflow(list) {
            if (list.scrollWidth > list.clientWidth) {
                list.classList.add('has-overflow');
            } else {
                list.classList.remove('has-overflow');
            }
        }
    };

    // =========================================================================
    // Ticker (Live Scores Carousel)
    // =========================================================================
    const Ticker = {
        init() {
            const viewport = document.querySelector('.ticker__viewport');
            if (!viewport) return;

            this.viewport = viewport;
            this.track = viewport.querySelector('.ticker__track');
            this.prevBtn = viewport.querySelector('.ticker__nav--prev');
            this.nextBtn = viewport.querySelector('.ticker__nav--next');

            if (!this.track) return;

            this.bindEvents();
            this.updateNavState();
            viewport.classList.add('is-ready');
        },

        bindEvents() {
            const track = this.track;
            const viewport = this.viewport;

            // Scroll event with throttle
            let ticking = false;
            track.addEventListener('scroll', () => {
                if (!ticking) {
                    requestAnimationFrame(() => {
                        this.updateNavState();
                        ticking = false;
                    });
                    ticking = true;
                }
            }, { passive: true });

            // Navigation buttons
            if (this.prevBtn) {
                this.prevBtn.addEventListener('click', () => this.scroll(-1));
            }
            if (this.nextBtn) {
                this.nextBtn.addEventListener('click', () => this.scroll(1));
            }

            // Mouse/Touch drag scrolling - smooth 1:1 movement
            let isDown = false;
            let startX = 0;
            let scrollStart = 0;

            const onPointerDown = (e) => {
                if (e.button && e.button !== 0) return;
                isDown = true;
                track.classList.add('is-dragging');
                startX = e.clientX || (e.touches && e.touches[0].clientX) || 0;
                scrollStart = track.scrollLeft;
                e.preventDefault();
            };

            const onPointerMove = (e) => {
                if (!isDown) return;
                const x = e.clientX || (e.touches && e.touches[0].clientX) || 0;
                const walk = startX - x;
                track.scrollLeft = scrollStart + walk;
            };

            const onPointerUp = () => {
                if (!isDown) return;
                isDown = false;
                track.classList.remove('is-dragging');
            };

            // Mouse events
            track.addEventListener('mousedown', onPointerDown);
            track.addEventListener('mousemove', onPointerMove);
            track.addEventListener('mouseup', onPointerUp);
            track.addEventListener('mouseleave', onPointerUp);

            // Touch events for mobile
            track.addEventListener('touchstart', onPointerDown, { passive: false });
            track.addEventListener('touchmove', onPointerMove, { passive: true });
            track.addEventListener('touchend', onPointerUp);

            // Prevent click on links after drag
            track.addEventListener('click', (e) => {
                if (Math.abs(track.scrollLeft - scrollStart) > 5) {
                    e.preventDefault();
                }
            }, { capture: true });
        },

        scroll(direction) {
            const scrollAmount = 300;
            this.track.scrollBy({
                left: direction * scrollAmount,
                behavior: 'smooth'
            });
        },

        updateNavState() {
            const { scrollLeft, scrollWidth, clientWidth } = this.track;
            const canPrev = scrollLeft > 5;
            const canNext = scrollLeft < scrollWidth - clientWidth - 5;

            this.viewport.classList.toggle('can-prev', canPrev);
            this.viewport.classList.toggle('can-next', canNext);
        }
    };

    // =========================================================================
    // Club Rail (Teams Carousel in Drawer)
    // =========================================================================
    const ClubRail = {
        init() {
            document.querySelectorAll('.club-rail').forEach(rail => {
                this.setup(rail);
            });
        },

        setup(rail) {
            const track = rail.querySelector('.club-rail__track');
            const prevBtn = rail.querySelector('.club-rail__nav--prev');
            const nextBtn = rail.querySelector('.club-rail__nav--next');

            if (!track) return;

            // Update navigation state
            const updateNav = () => {
                const { scrollLeft, scrollWidth, clientWidth } = track;
                const canPrev = scrollLeft > 5;
                const canNext = scrollLeft < scrollWidth - clientWidth - 5;

                rail.classList.toggle('can-prev', canPrev);
                rail.classList.toggle('can-next', canNext);
            };

            // Scroll event with throttle
            let ticking = false;
            track.addEventListener('scroll', () => {
                if (!ticking) {
                    requestAnimationFrame(() => {
                        updateNav();
                        ticking = false;
                    });
                    ticking = true;
                }
            }, { passive: true });

            // Navigation buttons
            if (prevBtn) {
                prevBtn.addEventListener('click', () => {
                    track.scrollBy({ left: -200, behavior: 'smooth' });
                });
            }
            if (nextBtn) {
                nextBtn.addEventListener('click', () => {
                    track.scrollBy({ left: 200, behavior: 'smooth' });
                });
            }

            // Mouse drag scrolling - smooth 1:1 movement
            let isDown = false;
            let startX = 0;
            let scrollStart = 0;

            const onPointerDown = (e) => {
                // Only left mouse button or touch
                if (e.button && e.button !== 0) return;

                isDown = true;
                rail.classList.add('is-dragging');
                startX = e.clientX || (e.touches && e.touches[0].clientX) || 0;
                scrollStart = track.scrollLeft;

                // Prevent text selection
                e.preventDefault();
            };

            const onPointerMove = (e) => {
                if (!isDown) return;

                const x = e.clientX || (e.touches && e.touches[0].clientX) || 0;
                const walk = startX - x;
                track.scrollLeft = scrollStart + walk;
            };

            const onPointerUp = () => {
                if (!isDown) return;
                isDown = false;
                rail.classList.remove('is-dragging');
            };

            // Mouse events
            track.addEventListener('mousedown', onPointerDown);
            track.addEventListener('mousemove', onPointerMove);
            track.addEventListener('mouseup', onPointerUp);
            track.addEventListener('mouseleave', onPointerUp);

            // Touch events for mobile
            track.addEventListener('touchstart', onPointerDown, { passive: false });
            track.addEventListener('touchmove', onPointerMove, { passive: true });
            track.addEventListener('touchend', onPointerUp);

            // Prevent click on links after drag
            track.addEventListener('click', (e) => {
                if (Math.abs(track.scrollLeft - scrollStart) > 5) {
                    e.preventDefault();
                }
            }, { capture: true });

            // Initial state
            updateNav();
        }
    };

    // =========================================================================
    // Dropdown Navigation - CSS only, JS just for cleanup
    // =========================================================================
    const DropdownNav = {
        init() {
            // No JS needed - pure CSS hover
        }
    };

    // =========================================================================
    // Weather Slideshow
    // =========================================================================
    const WeatherSlideshow = {
        container: null,
        slides: [],
        currentIndex: 0,
        intervalId: null,
        INTERVAL: 5000, // 5 seconds between slides

        init() {
            this.container = document.getElementById('weather-slideshow');
            if (!this.container) return;

            this.slides = this.container.querySelectorAll('.weather-chip__slide');
            if (this.slides.length <= 1) return; // No need to slide if only one item

            // Mark first slide as active and container as ready
            this.slides[0].classList.add('is-active');
            this.container.classList.add('is-ready');

            // Start the slideshow
            this.intervalId = setInterval(() => this.showNext(), this.INTERVAL);
        },

        showNext() {
            const currentSlide = this.slides[this.currentIndex];
            this.currentIndex = (this.currentIndex + 1) % this.slides.length;
            const nextSlide = this.slides[this.currentIndex];

            // CSS handles the fade transition via opacity/visibility
            currentSlide.classList.remove('is-active');
            nextSlide.classList.add('is-active');
        }
    };

    // =========================================================================
    // Tabs Component (with animations)
    // =========================================================================
    const Tabs = {
        init() {
            document.querySelectorAll('[role="tablist"]').forEach(tablist => {
                this.setup(tablist);
            });
        },

        setup(tablist) {
            const tabs = tablist.querySelectorAll('[role="tab"]');
            const panels = [];

            tabs.forEach(tab => {
                const panelId = tab.getAttribute('aria-controls');
                const panel = document.getElementById(panelId);
                if (panel) panels.push(panel);

                tab.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.activate(tab, tabs, panels);
                });

                tab.addEventListener('keydown', (e) => {
                    const index = Array.from(tabs).indexOf(tab);
                    let newIndex = index;

                    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                        newIndex = (index + 1) % tabs.length;
                    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                        newIndex = (index - 1 + tabs.length) % tabs.length;
                    } else if (e.key === 'Home') {
                        newIndex = 0;
                    } else if (e.key === 'End') {
                        newIndex = tabs.length - 1;
                    } else {
                        return;
                    }

                    e.preventDefault();
                    tabs[newIndex].focus();
                    this.activate(tabs[newIndex], tabs, panels);
                });
            });
        },

        activate(activeTab, tabs, panels) {
            tabs.forEach((tab, i) => {
                const isActive = tab === activeTab;
                tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
                tab.setAttribute('tabindex', isActive ? '0' : '-1');

                if (panels[i]) {
                    if (isActive) {
                        panels[i].hidden = false;
                        // Replay animations for [data-anim] elements in this panel
                        StatsAnim.replayInPanel(panels[i]);
                    } else {
                        panels[i].hidden = true;
                    }
                }
            });
        }
    };

    // =========================================================================
    // Stats Animation (fixtures and standings)
    // =========================================================================
    const StatsAnim = {
        init() {
            // Skip if user prefers reduced motion
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

            document.querySelectorAll('[data-anim]').forEach(el => {
                this.prepare(el);
                this.observe(el);
            });

            // Initialize rolling numbers
            this.initRollingNumbers();
        },

        prepare(el) {
            // Add delays to child elements
            const rows = el.querySelectorAll('.fixture, tbody tr');
            rows.forEach((row, i) => {
                row.style.setProperty('--d', `${i * 60}ms`);
                // Add rolling delay to scores in this row
                const rollEls = row.querySelectorAll('[data-roll]');
                rollEls.forEach((rollEl, j) => {
                    rollEl.style.setProperty('--rd', `${j * 100}ms`);
                });
            });
            el.classList.add('anim-ready');
        },

        initRollingNumbers() {
            document.querySelectorAll('[data-roll]').forEach(el => {
                const value = parseInt(el.textContent, 10);
                if (isNaN(value) || value < 0 || value > 9) return;

                // Create rolling strip
                const roll = document.createElement('span');
                roll.className = 'roll';
                roll.setAttribute('aria-hidden', 'true');

                const strip = document.createElement('span');
                strip.className = 'roll__strip';
                strip.style.setProperty('--n', value);

                // Create digits 0-9
                for (let i = 0; i <= 9; i++) {
                    const digit = document.createElement('span');
                    digit.textContent = i;
                    strip.appendChild(digit);
                }

                roll.appendChild(strip);

                // Keep original text for accessibility
                const srText = document.createElement('span');
                srText.className = 'visually-hidden';
                srText.textContent = el.textContent;

                // Replace content
                el.textContent = '';
                el.appendChild(roll);
                el.appendChild(srText);
            });
        },

        observe(el) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        // Small delay to ensure CSS is ready
                        requestAnimationFrame(() => {
                            el.classList.add('is-in');
                        });
                        observer.unobserve(el);
                    }
                });
            }, { threshold: 0.1 });

            observer.observe(el);
        },

        replayInPanel(panel) {
            const animEls = panel.querySelectorAll('[data-anim]');
            animEls.forEach(el => {
                // Reset animation
                el.classList.remove('is-in');
                // Force reflow
                void el.offsetWidth;
                // Re-add class to trigger animation
                requestAnimationFrame(() => {
                    el.classList.add('is-in');
                });
            });
        }
    };

    // =========================================================================
    // Chipset - sliding pill animation
    // =========================================================================
    const Chipset = {
        init() {
            document.querySelectorAll('[data-chipset]').forEach(chipset => {
                this.setup(chipset);
            });
        },

        setup(chipset) {
            const chips = chipset.querySelectorAll('.chip');
            if (!chips.length) return;

            // Skip if reduced motion
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

            // Add has-pill class
            chipset.classList.add('has-pill');

            // Find active chip - supports both aria-pressed (buttons) and aria-current (links)
            const active = chipset.querySelector('.chip[aria-pressed="true"], .chip[aria-current="page"]') || chips[0];

            // Set initial pill position
            if (active) {
                this.movePill(chipset, active);
                // Scroll active chip into view (centered)
                this.scrollToChip(chipset, active);
            }

            // Setup drag scrolling
            this.setupDrag(chipset);

            // Setup navigation arrows and gradients
            this.setupNav(chipset);

            // Handle resize
            if ('ResizeObserver' in window) {
                const ro = new ResizeObserver(() => {
                    const current = chipset.querySelector('.chip[aria-pressed="true"], .chip[aria-current="page"]');
                    if (current) this.movePill(chipset, current);
                });
                ro.observe(chipset);
            }

            // Reposition after fonts load
            document.fonts?.ready.then(() => {
                const current = chipset.querySelector('.chip[aria-pressed="true"], .chip[aria-current="page"]');
                if (current) this.movePill(chipset, current);
            });

            // Handle clicks for button-based chipsets (with aria-pressed)
            chips.forEach(chip => {
                if (chip.tagName === 'BUTTON' || chip.hasAttribute('aria-pressed')) {
                    chip.addEventListener('click', (e) => {
                        e.preventDefault();
                        const prev = chipset.querySelector('.chip[aria-pressed="true"]');
                        if (prev === chip) return;

                        // Determine direction
                        const prevIndex = Array.from(chips).indexOf(prev);
                        const newIndex = Array.from(chips).indexOf(chip);
                        chipset.dataset.dir = newIndex > prevIndex ? 'fwd' : 'back';

                        // Update states
                        if (prev) {
                            prev.setAttribute('aria-pressed', 'false');
                            prev.classList.add('is-leaving');
                            setTimeout(() => prev.classList.remove('is-leaving'), 500);
                        }
                        chip.setAttribute('aria-pressed', 'true');

                        // Animate pill
                        chipset.classList.remove('is-instant');
                        this.movePill(chipset, chip);

                        // Trigger custom event
                        chipset.dispatchEvent(new CustomEvent('chipchange', {
                            detail: { chip, filter: chip.textContent.trim() }
                        }));
                    });
                }
            });
        },

        movePill(chipset, chip) {
            const rect = chip.getBoundingClientRect();
            const parentRect = chipset.getBoundingClientRect();
            const scrollLeft = chipset.scrollLeft || 0;

            chipset.style.setProperty('--pill-l', `${rect.left - parentRect.left + scrollLeft}px`);
            chipset.style.setProperty('--pill-r', `${parentRect.right - rect.right - scrollLeft}px`);
            chipset.style.setProperty('--pill-t', `${rect.top - parentRect.top}px`);
            chipset.style.setProperty('--pill-h', `${rect.height}px`);
        },

        scrollToChip(chipset, chip) {
            // Only scroll if chipset is scrollable
            if (chipset.scrollWidth <= chipset.clientWidth) return;

            // Calculate scroll position to center the chip
            const chipRect = chip.getBoundingClientRect();
            const chipsetRect = chipset.getBoundingClientRect();
            const scrollLeft = chip.offsetLeft - (chipset.clientWidth - chip.offsetWidth) / 2;

            chipset.scrollTo({
                left: Math.max(0, scrollLeft),
                behavior: 'instant'
            });
        },

        setupDrag(chipset) {
            const self = this;
            let isDown = false;
            let startX = 0;
            let scrollStart = 0;
            let moved = false;

            const onMouseDown = (e) => {
                if (e.button !== 0) return;

                isDown = true;
                moved = false;
                startX = e.clientX;
                scrollStart = chipset.scrollLeft;
                chipset.style.cursor = 'grabbing';
            };

            const onMouseMove = (e) => {
                if (!isDown) return;

                const dx = e.clientX - startX;
                if (Math.abs(dx) > 5) {
                    moved = true;
                    chipset.classList.add('is-dragging');
                }
                chipset.scrollLeft = scrollStart - dx;
            };

            const onMouseUp = () => {
                if (!isDown) return;

                isDown = false;
                chipset.style.cursor = '';
                chipset.classList.remove('is-dragging');

                // Update pill position after drag
                const current = chipset.querySelector('.chip[aria-pressed="true"], .chip[aria-current="page"]');
                if (current) self.movePill(chipset, current);
            };

            // Prevent click on links after drag
            const onClick = (e) => {
                if (moved) {
                    e.preventDefault();
                    e.stopPropagation();
                    moved = false;
                }
            };

            chipset.addEventListener('mousedown', onMouseDown);
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
            chipset.addEventListener('mouseleave', () => {
                if (isDown && !moved) {
                    isDown = false;
                    chipset.style.cursor = '';
                }
            });
            chipset.addEventListener('click', onClick, { capture: true });

            // Prevent text selection
            chipset.style.userSelect = 'none';

            // Prevent native drag on links
            chipset.querySelectorAll('a').forEach(a => {
                a.addEventListener('dragstart', e => e.preventDefault());
            });
        },

        setupNav(chipset) {
            const wrap = chipset.closest('.chips-wrap');
            if (!wrap) return;

            const prevBtn = wrap.querySelector('.chips-nav--prev');
            const nextBtn = wrap.querySelector('.chips-nav--next');

            const updateState = () => {
                const { scrollLeft, scrollWidth, clientWidth } = chipset;
                const canPrev = scrollLeft > 5;
                const canNext = scrollLeft < scrollWidth - clientWidth - 5;

                wrap.classList.toggle('can-prev', canPrev);
                wrap.classList.toggle('can-next', canNext);

                if (prevBtn) prevBtn.disabled = !canPrev;
                if (nextBtn) nextBtn.disabled = !canNext;
            };

            // Scroll handlers
            let ticking = false;
            chipset.addEventListener('scroll', () => {
                if (!ticking) {
                    requestAnimationFrame(() => {
                        updateState();
                        // Update pill position on scroll
                        const current = chipset.querySelector('.chip[aria-pressed="true"], .chip[aria-current="page"]');
                        if (current) this.movePill(chipset, current);
                        ticking = false;
                    });
                    ticking = true;
                }
            }, { passive: true });

            // Button click handlers
            const scrollAmount = 200;
            if (prevBtn) {
                prevBtn.addEventListener('click', () => {
                    chipset.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
                });
            }
            if (nextBtn) {
                nextBtn.addEventListener('click', () => {
                    chipset.scrollBy({ left: scrollAmount, behavior: 'smooth' });
                });
            }

            // Initial state
            updateState();
        }
    };

    // =========================================================================
    // BLITZ TV Player
    // =========================================================================
    const BlitzTV = {
        // Constants
        YT: 'https://www.youtube-nocookie.com',
        AUTO_KEY: 'bs-tv-auto',
        NEXT_COUNTDOWN: 5,

        // State
        tv: null,
        player: null,
        frame: null,
        poster: null,
        list: null,
        items: [],
        info: null,
        auto: null,
        next: null,
        dockBar: null,
        cur: 0,
        iframe: null,
        state: -1,
        dismissed: false,
        timer: 0,
        reduce: false,
        wide: null,
        offscreen: false,

        init() {
            this.tv = document.querySelector('[data-tv]');
            if (!this.tv) return;

            this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            this.wide = window.matchMedia('(min-width: 700px)');

            // Cache elements
            this.player = this.tv.querySelector('[data-tv-player]');
            this.frame = this.tv.querySelector('[data-tv-frame]');
            this.poster = this.tv.querySelector('[data-tv-play]');
            this.list = this.tv.querySelector('[data-tv-list]');
            this.items = [...this.tv.querySelectorAll('.tv-item')];
            this.info = this.tv.querySelector('[data-tv-info]');
            this.auto = this.tv.querySelector('[data-tv-auto]');
            this.next = this.tv.querySelector('[data-tv-next]');
            this.dockBar = this.tv.querySelector('.tv-dock-bar');

            if (!this.player || !this.frame || !this.items.length) return;

            // Load auto-play preference
            this.loadAutoPreference();

            // Bind events
            this.bindEvents();

            // Setup dock observer
            this.setupDockObserver();
        },

        loadAutoPreference() {
            try {
                const v = localStorage.getItem(this.AUTO_KEY);
                if (v !== null && this.auto) {
                    this.auto.checked = v === '1';
                }
            } catch { /* localStorage unavailable */ }
        },

        bindEvents() {
            const self = this;

            // Auto-play toggle
            if (this.auto) {
                this.auto.addEventListener('change', () => {
                    try {
                        localStorage.setItem(self.AUTO_KEY, self.auto.checked ? '1' : '0');
                    } catch { /* */ }
                });
            }

            // Poster click - start playing
            if (this.poster) {
                this.poster.addEventListener('click', (e) => {
                    if (!this.plainClick(e)) return;
                    e.preventDefault();
                    self.start();
                });
            }

            // Playlist item click
            if (this.list) {
                this.list.addEventListener('click', (e) => {
                    const a = e.target.closest('.tv-item');
                    if (!a || !this.plainClick(e)) return;
                    e.preventDefault();
                    self.show(self.items.indexOf(a), true);
                    // Scroll player into view if needed
                    const r = self.player.getBoundingClientRect();
                    if (r.top < 60 || r.bottom > window.innerHeight) {
                        self.player.scrollIntoView({
                            behavior: self.reduce ? 'auto' : 'smooth',
                            block: 'center'
                        });
                    }
                });
            }

            // Preconnect to YouTube on hover/focus
            this.tv.addEventListener('pointerenter', () => this.preconnect(this.YT), { once: true });
            this.tv.addEventListener('focusin', () => this.preconnect(this.YT), { once: true });

            // Share button
            const shareBtn = this.tv.querySelector('[data-tv-share]');
            if (shareBtn) {
                shareBtn.addEventListener('click', (e) => {
                    const d = self.items[self.cur].dataset;
                    self.share(e.currentTarget, d.title, `https://www.youtube.com/watch?v=${d.yt}`);
                });
            }

            // "Up Next" buttons
            const nextGoBtn = this.tv.querySelector('[data-tv-next-go]');
            const nextCancelBtn = this.tv.querySelector('[data-tv-next-cancel]');
            if (nextGoBtn) {
                nextGoBtn.addEventListener('click', () => self.show(self.cur + 1, true));
            }
            if (nextCancelBtn) {
                nextCancelBtn.addEventListener('click', () => {
                    clearInterval(self.timer);
                    self.next.hidden = true;
                });
            }

            // Dock bar buttons
            const backBtn = this.tv.querySelector('[data-tv-back]');
            const closeBtn = this.tv.querySelector('[data-tv-close]');
            if (backBtn) {
                backBtn.addEventListener('click', () => {
                    self.player.scrollIntoView({
                        behavior: self.reduce ? 'auto' : 'smooth',
                        block: 'center'
                    });
                });
            }
            if (closeBtn) {
                closeBtn.addEventListener('click', () => {
                    self.post('pauseVideo');
                    self.dismissed = true;
                    self.undock();
                });
            }

            // Listen for YouTube player state changes
            window.addEventListener('message', (e) => {
                if (!self.iframe || e.source !== self.iframe.contentWindow) return;
                let m;
                try {
                    m = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
                } catch { return; }

                const s = m?.event === 'onStateChange' ? m.info :
                          m?.event === 'infoDelivery' ? m.info?.playerState : undefined;
                if (typeof s !== 'number' || s === self.state) return;

                self.state = s;
                if (s === 0) self.upNext();         // Video ended
                if (s === 1) {                      // Playing
                    self.next.hidden = true;
                    clearInterval(self.timer);
                }
                self.syncDock();
            });
        },

        setupDockObserver() {
            const self = this;
            new IntersectionObserver(([en]) => {
                self.offscreen = !en.isIntersecting;
                if (!self.offscreen) self.dismissed = false;
                self.syncDock();
            }, { threshold: 0.25 }).observe(this.player);
        },

        plainClick(e) {
            return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
        },

        preconnect(origin) {
            if (this._preconnected?.has(origin)) return;
            if (!this._preconnected) this._preconnected = new Set();
            this._preconnected.add(origin);
            const l = document.createElement('link');
            l.rel = 'preconnect';
            l.href = origin;
            l.crossOrigin = '';
            document.head.append(l);
        },

        post(func, args = []) {
            this.iframe?.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func, args, id: 1, channel: 'widget' }),
                '*'
            );
        },

        ytThumb(id, size = 'maxresdefault') {
            return `https://i.ytimg.com/vi/${id}/${size}.jpg`;
        },

        stop() {
            clearInterval(this.timer);
            if (this.next) this.next.hidden = true;
            this.undock();
            this.iframe?.remove();
            this.iframe = null;
            this.state = -1;
            this.frame.classList.remove('is-playing');
        },

        start() {
            this.stop();
            const d = this.items[this.cur].dataset;
            const origin = location.origin.startsWith('http')
                ? `&origin=${encodeURIComponent(location.origin)}`
                : '';

            this.iframe = document.createElement('iframe');
            this.iframe.title = d.title;
            this.iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
            this.iframe.allowFullscreen = true;
            this.iframe.referrerPolicy = 'strict-origin-when-cross-origin';
            this.iframe.src = `${this.YT}/embed/${d.yt}?autoplay=1&rel=0&playsinline=1&enablejsapi=1${origin}`;

            const self = this;
            this.iframe.addEventListener('load', function() {
                const w = self.iframe?.contentWindow;
                if (!w) return;
                w.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), '*');
                self.post('addEventListener', ['onStateChange']);
            });

            this.frame.append(this.iframe);
            this.frame.classList.add('is-playing');
            this.dismissed = false;
            this.state = 3; // buffering
        },

        show(i, play) {
            if (i < 0 || i >= this.items.length) return;
            this.cur = i;
            const d = this.items[i].dataset;

            // Update aria-current on items
            this.items.forEach((a, k) => {
                if (k === i) {
                    a.setAttribute('aria-current', 'true');
                } else {
                    a.removeAttribute('aria-current');
                }
            });

            // Update poster
            const pic = this.poster?.querySelector('img');
            if (pic) {
                pic.src = this.ytThumb(d.yt, 'maxresdefault');
            }
            if (this.poster) {
                this.poster.href = `https://www.youtube.com/watch?v=${d.yt}`;
            }

            // Update screen reader text
            const srEl = this.tv.querySelector('[data-tv-sr]');
            if (srEl) srEl.textContent = d.title;

            // Update duration on poster
            const durEl = this.tv.querySelector('[data-tv-dur]');
            if (durEl) durEl.textContent = d.dur;

            // Update ambient background
            const ambImg = this.tv.querySelector('.tv-ambient img');
            if (ambImg) ambImg.src = this.ytThumb(d.yt, 'mqdefault');

            // Update info section
            const titleEl = this.tv.querySelector('[data-tv-title]');
            if (titleEl) titleEl.textContent = d.title;

            const dockTitleEl = this.tv.querySelector('[data-tv-dock-title]');
            if (dockTitleEl) dockTitleEl.textContent = d.title;

            const whenEl = this.tv.querySelector('[data-tv-when]');
            if (whenEl) {
                whenEl.textContent = d.when;
                if (d.iso) whenEl.dateTime = d.iso;
            }

            const viewsEl = this.tv.querySelector('[data-tv-views]');
            if (viewsEl) viewsEl.textContent = d.views;

            const lenEl = this.tv.querySelector('[data-tv-len]');
            if (lenEl) lenEl.textContent = d.dur;

            const ytLink = this.tv.querySelector('[data-tv-yt]');
            if (ytLink) ytLink.href = `https://www.youtube.com/watch?v=${d.yt}`;

            // Toggle "New" badge
            const newBadge = this.tv.querySelector('.tv-new');
            if (newBadge) newBadge.hidden = d.new !== '1';

            // Animate info section
            if (this.info && !this.reduce) {
                [...this.info.children].forEach((el, k) => this.rise(el, 10, k * 50));
            }

            // Scroll active item into view in playlist
            const li = this.items[i].parentElement;
            if (this.list && li) {
                const padLeft = parseFloat(getComputedStyle(this.list).scrollPaddingInlineStart || 0);
                this.list.scrollTo({
                    top: li.offsetTop - 8,
                    left: li.offsetLeft - padLeft,
                    behavior: this.reduce ? 'auto' : 'smooth'
                });
            }

            // Play or stop
            if (play) {
                this.start();
            } else {
                this.stop();
            }
        },

        rise(el, dy = 12, delay = 0) {
            if (this.reduce) return;
            el.animate(
                [
                    { opacity: 0, transform: `translateY(${dy}px)` },
                    { opacity: 1, transform: 'none' }
                ],
                { duration: 380, delay, easing: 'cubic-bezier(.2, .7, .2, 1)', fill: 'backwards' }
            );
        },

        upNext() {
            if (!this.auto?.checked || this.cur >= this.items.length - 1) return;
            this.undock();

            const n = this.items[this.cur + 1].dataset;
            let left = this.NEXT_COUNTDOWN;

            const nextTitleEl = this.tv.querySelector('[data-tv-next-title]');
            const nextCountEl = this.tv.querySelector('[data-tv-next-count]');
            if (nextTitleEl) nextTitleEl.textContent = n.title;
            if (nextCountEl) nextCountEl.textContent = left;

            this.next.style.setProperty('--next-s', `${this.NEXT_COUNTDOWN}s`);
            this.next.hidden = false;
            this.rise(this.next, 0);

            const nextGoBtn = this.tv.querySelector('[data-tv-next-go]');
            if (nextGoBtn) nextGoBtn.focus({ preventScroll: true });

            clearInterval(this.timer);
            const self = this;
            this.timer = setInterval(() => {
                left -= 1;
                if (nextCountEl) nextCountEl.textContent = left;
                if (left <= 0) {
                    clearInterval(self.timer);
                    self.show(self.cur + 1, true);
                }
            }, 1000);
        },

        docked() {
            return this.frame.classList.contains('is-docked');
        },

        undock() {
            if (!this.docked()) return;
            this.frame.classList.remove('is-docked');
            this.player.classList.remove('is-docked');
            if (this.dockBar) this.dockBar.hidden = true;
            this.rise(this.frame, 0);
        },

        syncDock() {
            const want = this.iframe &&
                         this.offscreen &&
                         !this.dismissed &&
                         this.wide.matches &&
                         (this.state === 1 || this.state === 3);

            if (want && !this.docked()) {
                this.frame.classList.add('is-docked');
                this.player.classList.add('is-docked');
                if (this.dockBar) this.dockBar.hidden = false;
                this.rise(this.frame, 24);
            } else if (!want && this.docked() && !(this.offscreen && this.state === 2)) {
                // On pause, keep mini player visible so user can resume
                this.undock();
            }
        },

        async share(btn, title, url) {
            // Always use native share when available
            if (navigator.share) {
                try {
                    await navigator.share({ title, url });
                } catch { /* cancelled */ }
                return;
            }
            // Fallback to clipboard copy
            try {
                await navigator.clipboard.writeText(url);
            } catch {
                return;
            }
            const label = btn.querySelector('span');
            if (!label) return;
            const was = label.textContent;
            label.textContent = 'Копирано';
            btn.classList.add('is-done');
            setTimeout(() => {
                label.textContent = was;
                btn.classList.remove('is-done');
            }, 1800);
        }
    };

    // =========================================================================
    // Reels (Кратки видеа) - Facebook Reels horizontal carousel with lightbox
    // =========================================================================
    const Reels = {
        FB: 'https://www.facebook.com',
        reduce: false,

        // State
        rs: null,
        track: null,
        cards: [],
        prevBtn: null,
        nextBtn: null,
        dlg: null,
        stage: null,
        poster: null,
        amb: null,
        segs: [],
        infoEl: null,
        up: null,
        down: null,
        ytBtn: null,
        at: 0,
        frame: null,
        lock: 0,

        init() {
            this.rs = document.querySelector('[data-reels]');
            if (!this.rs) return;

            this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            // Cache elements
            this.track = this.rs.querySelector('[data-reels-track]');
            this.cards = [...this.rs.querySelectorAll('[data-reel]')];
            this.prevBtn = this.rs.querySelector('[data-reels-prev]');
            this.nextBtn = this.rs.querySelector('[data-reels-next]');
            this.dlg = this.rs.querySelector('[data-reel-viewer]');
            this.stage = this.dlg?.querySelector('[data-rv-stage]');
            this.poster = this.dlg?.querySelector('[data-rv-poster]');
            this.amb = this.dlg?.querySelector('[data-rv-ambient]');
            this.segs = this.dlg ? [...this.dlg.querySelectorAll('.rv-progress i')] : [];
            this.infoEl = this.dlg?.querySelector('.rv-info');
            this.up = this.dlg?.querySelector('[data-rv-prev]');
            this.down = this.dlg?.querySelector('[data-rv-next]');
            this.ytBtn = this.dlg?.querySelector('[data-rv-yt]');

            if (!this.track || !this.cards.length) return;

            this.bindTrackEvents();
            this.bindDragEvents();
            this.bindViewerEvents();

            // Show nav buttons
            const nav = this.rs.querySelector('.reels-nav');
            if (nav) nav.hidden = false;

            // Preconnect to Facebook on hover
            this.rs.addEventListener('pointerenter', () => this.preconnect(this.FB), { once: true });
        },

        preconnect(origin) {
            if (this._preconnected?.has(origin)) return;
            if (!this._preconnected) this._preconnected = new Set();
            this._preconnected.add(origin);
            const l = document.createElement('link');
            l.rel = 'preconnect';
            l.href = origin;
            l.crossOrigin = '';
            document.head.append(l);
        },

        plainClick(e) {
            return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
        },

        bindTrackEvents() {
            const self = this;

            // Update edge fade masks and nav button states
            const edges = () => {
                const max = self.track.scrollWidth - self.track.clientWidth - 2;
                const x = Math.abs(self.track.scrollLeft);
                if (self.prevBtn) self.prevBtn.disabled = x <= 2;
                if (self.nextBtn) self.nextBtn.disabled = x >= max;
                self.track.classList.toggle('at-start', x <= 2);
                self.track.classList.toggle('at-end', x >= max);
            };

            this.track.addEventListener('scroll', edges, { passive: true });
            new ResizeObserver(edges).observe(this.track);
            edges();

            // Navigation buttons
            const page = (dir) => this.track.scrollBy({
                left: dir * this.track.clientWidth * 0.8,
                behavior: this.reduce ? 'auto' : 'smooth'
            });
            if (this.prevBtn) this.prevBtn.addEventListener('click', () => page(-1));
            if (this.nextBtn) this.nextBtn.addEventListener('click', () => page(1));
        },

        bindDragEvents() {
            const self = this;
            const track = this.track;
            let drag = null, dragged = false, settle = 0;

            // Find closest card start position for snap
            const snapTo = (x) => {
                const pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
                const box = track.getBoundingClientRect();
                const max = track.scrollWidth - track.clientWidth;
                let best = x, dist = Infinity;
                for (const li of track.children) {
                    const at = Math.min(max, Math.max(0, track.scrollLeft + li.getBoundingClientRect().left - box.left - pad));
                    if (Math.abs(at - x) < dist) { dist = Math.abs(at - x); best = at; }
                }
                return best;
            };

            const release = () => { clearTimeout(settle); track.classList.remove('is-dragging'); };

            track.addEventListener('pointerdown', (e) => {
                if (e.pointerType !== 'mouse' || e.button !== 0) return;
                release();
                drag = { x: e.clientX, left: track.scrollLeft, id: e.pointerId, t: e.timeStamp, v: 0 };
                dragged = false;
            });

            track.addEventListener('pointermove', (e) => {
                if (!drag) return;
                const dx = e.clientX - drag.x;
                if (!dragged && Math.abs(dx) < 6) return;
                if (!dragged) {
                    dragged = true;
                    track.setPointerCapture(drag.id);
                    track.classList.add('is-dragging');
                }
                const dt = e.timeStamp - drag.t;
                if (dt > 0) drag.v = 0.8 * ((track.scrollLeft - (drag.left - dx)) / dt) + 0.2 * drag.v;
                drag.t = e.timeStamp;
                track.scrollLeft = drag.left - dx;
            });

            const endDrag = (e) => {
                if (!drag) return;
                const v = e.timeStamp - drag.t > 90 ? 0 : drag.v;
                drag = null;
                if (!dragged) return;
                const target = snapTo(track.scrollLeft - v * 260);
                track.scrollTo({ left: target, behavior: self.reduce ? 'auto' : 'smooth' });

                const done = () => {
                    if (Math.abs(track.scrollLeft - target) > 2) return;
                    track.removeEventListener('scrollend', done);
                    release();
                };
                track.addEventListener('scrollend', done);
                settle = setTimeout(() => { track.removeEventListener('scrollend', done); release(); }, 900);
            };

            track.addEventListener('pointerup', endDrag);
            track.addEventListener('pointercancel', endDrag);
            track.addEventListener('click', (e) => {
                if (!dragged) return;
                e.preventDefault();
                e.stopPropagation();
                dragged = false;
            }, true);
            track.addEventListener('dragstart', (e) => e.preventDefault());
        },

        bindViewerEvents() {
            if (!this.dlg) return;
            const self = this;

            // Open viewer on card click
            this.track.addEventListener('click', (e) => {
                const a = e.target.closest('[data-reel]');
                if (!a || !self.plainClick(e) || typeof self.dlg.showModal !== 'function') return;
                e.preventDefault();
                self.dlg.showModal();
                self.go(self.cards.indexOf(a));
                const closeBtn = self.dlg.querySelector('[data-rv-close]');
                if (closeBtn) closeBtn.focus({ preventScroll: true });
            });

            // Close viewer
            this.dlg.addEventListener('close', () => {
                self.frame?.remove();
                self.frame = null;
                self.cards[self.at].focus({ preventScroll: true });
                self.cards[self.at].scrollIntoView({
                    behavior: self.reduce ? 'auto' : 'smooth',
                    block: 'nearest',
                    inline: 'nearest'
                });
            });

            const closeBtn = this.dlg.querySelector('[data-rv-close]');
            if (closeBtn) closeBtn.addEventListener('click', () => self.dlg.close());

            // Navigation buttons
            if (this.up) this.up.addEventListener('click', () => self.step(-1));
            if (this.down) this.down.addEventListener('click', () => self.step(1));

            // Share button
            const shareBtn = this.dlg.querySelector('[data-rv-share]');
            if (shareBtn) {
                shareBtn.addEventListener('click', (e) => {
                    const d = self.cards[self.at].dataset;
                    self.share(e.currentTarget, d.cap, `${self.FB}/reel/${d.reel}`);
                });
            }

            // Keyboard navigation
            this.dlg.addEventListener('keydown', (e) => {
                if (e.target.closest('a, button') && (e.key === ' ' || e.key === 'Enter')) return;
                const dir = { ArrowDown: 1, ArrowRight: 1, PageDown: 1, j: 1, ArrowUp: -1, ArrowLeft: -1, PageUp: -1, k: -1 }[e.key];
                if (!dir) return;
                e.preventDefault();
                self.step(dir);
            });

            // Mouse wheel navigation
            this.dlg.addEventListener('wheel', (e) => {
                if (Math.abs(e.deltaY) < 24) return;
                e.preventDefault();
                self.step(e.deltaY > 0 ? 1 : -1);
            }, { passive: false });

            // Touch swipe navigation
            let y0 = null, swiped = false;
            this.dlg.addEventListener('pointerdown', (e) => {
                swiped = false;
                if (e.pointerType !== 'mouse') y0 = e.clientY;
            });
            this.dlg.addEventListener('pointerup', (e) => {
                if (y0 === null) return;
                const dy = e.clientY - y0;
                y0 = null;
                if (Math.abs(dy) > 50) { swiped = true; self.step(dy < 0 ? 1 : -1); }
            });

            // Click outside to close
            this.dlg.addEventListener('click', (e) => {
                if (!swiped && (e.target === self.dlg || e.target.classList.contains('rv-body'))) {
                    self.dlg.close();
                }
            });
        },

        go(i, dir = 0) {
            if (i < 0 || i >= this.cards.length) return;
            this.at = i;
            const d = this.cards[i].dataset;

            // Set aspect ratio
            if (this.dlg) this.dlg.style.setProperty('--ar', d.ar || '0.5625');

            // Update poster and ambient (placeholder - no actual FB images without API)
            if (this.poster) this.poster.src = '';
            if (this.amb) this.amb.src = '';

            // Clear and recreate iframe
            this.stage?.classList.remove('is-ready');
            this.frame?.remove();

            const r = this.stage?.getBoundingClientRect();
            if (r && this.stage) {
                this.frame = document.createElement('iframe');
                this.frame.title = d.cap;
                this.frame.allow = 'autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share';
                this.frame.allowFullscreen = true;
                this.frame.scrolling = 'no';
                this.frame.src = `${this.FB}/plugins/video.php?href=${encodeURIComponent(`${this.FB}/reel/${d.reel}/`)}&show_text=false&autoplay=true&width=${Math.round(r.width)}&height=${Math.round(r.height)}`;
                this.frame.addEventListener('load', () => this.stage.classList.add('is-ready'), { once: true });
                this.stage.append(this.frame);
            }

            // Update caption
            const capEl = this.dlg?.querySelector('[data-rv-cap]');
            if (capEl) capEl.textContent = d.cap;

            // Update meta
            const metaEl = this.dlg?.querySelector('[data-rv-meta]');
            if (metaEl) {
                const ico = (id) => `<svg class="i" aria-hidden="true"><use href="#${id}"></use></svg>`;
                const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
                const meta = [];
                if (d.plays) meta.push(`<span>${ico('i-play-fill')}${esc(d.plays)} гледания</span>`);
                if (d.reactions && d.reactions !== '0') meta.push(`<span>${ico('i-thumbs-up-fill')}${esc(d.reactions)}</span>`);
                meta.push(`<span>${ico('i-clock')}<time datetime="${esc(d.iso)}">${esc(d.when)}</time></span>`);
                metaEl.innerHTML = meta.join('');
            }

            // Update Facebook link
            const fbLink = this.dlg?.querySelector('[data-rv-fb]');
            if (fbLink) fbLink.href = `${this.FB}/reel/${d.reel}`;

            // Toggle YouTube button
            if (this.ytBtn) {
                this.ytBtn.hidden = !d.yt;
                if (d.yt) this.ytBtn.href = `https://www.youtube.com/watch?v=${d.yt}`;
            }

            // Update counter
            const countEl = this.dlg?.querySelector('[data-rv-count]');
            if (countEl) countEl.textContent = i + 1;

            // Update progress indicators
            this.segs.forEach((s, k) => {
                s.classList.toggle('is-seen', k < i);
                s.classList.toggle('is-current', k === i);
            });

            // Update nav button states
            if (this.up) this.up.disabled = i === 0;
            if (this.down) this.down.disabled = i === this.cards.length - 1;

            // Animate transition
            if (dir && !this.reduce && this.stage && this.infoEl) {
                this.stage.animate(
                    [{ opacity: 0, transform: `translateY(${dir * 48}px) scale(.97)` }, { opacity: 1, transform: 'none' }],
                    { duration: 360, easing: 'cubic-bezier(.2, .7, .2, 1)' }
                );
                this.rise(this.infoEl, dir * 16, 60);
            }

            // Preload adjacent thumbnails (when we have real images)
        },

        step(dir) {
            const now = Date.now();
            if (now < this.lock) return;
            this.lock = now + 450;
            this.go(this.at + dir, dir);
        },

        rise(el, dy = 12, delay = 0) {
            if (this.reduce) return;
            el.animate(
                [{ opacity: 0, transform: `translateY(${dy}px)` }, { opacity: 1, transform: 'none' }],
                { duration: 380, delay, easing: 'cubic-bezier(.2, .7, .2, 1)', fill: 'backwards' }
            );
        },

        async share(btn, title, url) {
            if (navigator.share) {
                try { await navigator.share({ title, url }); } catch { /* cancelled */ }
                return;
            }
            try { await navigator.clipboard.writeText(url); } catch { return; }
            const label = btn.querySelector('span');
            if (!label) return;
            const was = label.textContent;
            label.textContent = 'Копирано';
            btn.classList.add('is-done');
            setTimeout(() => { label.textContent = was; btn.classList.remove('is-done'); }, 1800);
        }
    };

    // =========================================================================
    // To Top Button
    // =========================================================================
    const ToTop = {
        init() {
            const btn = document.querySelector('[data-to-top]');
            if (!btn) return;

            btn.addEventListener('click', (e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
    };

    // =========================================================================
    // Mobile Tabbar
    // =========================================================================
    const Tabbar = {
        el: null,
        lastY: 0,
        threshold: 10,

        init() {
            this.el = document.querySelector('.tabbar');
            if (!this.el) return;

            // Hide on scroll down, show on scroll up
            let ticking = false;
            window.addEventListener('scroll', () => {
                if (ticking) return;
                ticking = true;
                requestAnimationFrame(() => {
                    this.onScroll();
                    ticking = false;
                });
            }, { passive: true });
        },

        onScroll() {
            const y = window.scrollY;
            const delta = y - this.lastY;

            if (Math.abs(delta) > this.threshold) {
                if (delta > 0 && y > 100) {
                    this.el.classList.add('is-hidden');
                } else {
                    this.el.classList.remove('is-hidden');
                }
                this.lastY = y;
            }
        }
    };

    // =========================================================================
    // My Team (Favorite Team Selection)
    // =========================================================================
    const MyTeam = {
        STORAGE_KEY: 'myTeam',
        favChip: null,
        dialog: null,

        init() {
            this.favChip = document.querySelector('.fav-chip');
            this.dialog = document.getElementById('myteam');
            if (!this.dialog) return;

            this.loadSavedTeam();
            this.bindEvents();
        },

        loadSavedTeam() {
            try {
                const saved = localStorage.getItem(this.STORAGE_KEY);
                if (saved) {
                    const team = JSON.parse(saved);
                    this.applyTeam(team);
                }
            } catch (e) {
                // localStorage not available
            }
        },

        saveTeam(team) {
            try {
                if (team) {
                    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(team));
                } else {
                    localStorage.removeItem(this.STORAGE_KEY);
                }
            } catch (e) {
                // localStorage not available
            }
        },

        applyTeam(team) {
            if (!this.favChip) return;

            // Update fav-chip appearance
            const titleEl = this.favChip.querySelector('.fav-chip__title');
            const subEl = this.favChip.querySelector('.fav-chip__sub');
            const iconEl = this.favChip.querySelector('.fav-chip__icon');
            const mediaEl = this.favChip.querySelector('.fav-chip__media');

            if (team && team.name) {
                this.favChip.classList.add('has-team');
                if (titleEl) titleEl.textContent = team.name;
                if (subEl) subEl.textContent = 'Моят отбор';

                // If team has crest image
                if (team.crest) {
                    let crestImg = mediaEl.querySelector('.fav-chip__crest');
                    if (!crestImg) {
                        crestImg = document.createElement('img');
                        crestImg.className = 'fav-chip__crest';
                        crestImg.alt = '';
                        mediaEl.appendChild(crestImg);
                    }
                    crestImg.src = team.crest;
                }
            } else {
                this.favChip.classList.remove('has-team');
                if (titleEl) titleEl.textContent = 'Моят отбор';
                if (subEl) subEl.textContent = 'Избери любим отбор';
                const crestImg = mediaEl?.querySelector('.fav-chip__crest');
                if (crestImg) crestImg.remove();
            }

            // Update selected state in dialog
            this.dialog.querySelectorAll('.team-option').forEach(opt => {
                opt.classList.toggle('is-selected', team && opt.dataset.teamId === team.id);
            });
        },

        bindEvents() {
            // Team selection
            this.dialog.addEventListener('click', (e) => {
                const teamBtn = e.target.closest('.team-option');
                if (teamBtn) {
                    const team = {
                        id: teamBtn.dataset.teamId,
                        name: teamBtn.dataset.teamName,
                        crest: teamBtn.querySelector('img')?.src || null
                    };
                    this.saveTeam(team);
                    this.applyTeam(team);

                    // Close dialog
                    this.dialog.close();
                    document.body.classList.remove('is-locked');
                }

                // Clear team
                const clearBtn = e.target.closest('[data-clear-team]');
                if (clearBtn) {
                    this.saveTeam(null);
                    this.applyTeam(null);
                }
            });
        }
    };

    // =========================================================================
    // Comments Toggle (Article page)
    // =========================================================================
    const CommentsToggle = {
        init() {
            const toggle = document.querySelector('.cmts__toggle');
            const panel = document.getElementById('cmts-panel');
            if (!toggle || !panel) return;

            toggle.addEventListener('click', () => {
                const expanded = toggle.getAttribute('aria-expanded') === 'true';
                toggle.setAttribute('aria-expanded', !expanded);
                panel.hidden = expanded;

                const label = toggle.querySelector('.cmts__toggle-label');
                if (label) {
                    label.textContent = expanded ? 'Покажи коментарите' : 'Скрий коментарите';
                }

                // Rotate caret
                const caret = toggle.querySelector('.cmts__caret');
                if (caret) {
                    caret.style.transform = expanded ? '' : 'rotate(180deg)';
                }
            });

            // Reply button handlers
            document.querySelectorAll('.comment__reply-btn').forEach(btn => {
                btn.addEventListener('click', () => {
                    const replyForm = btn.closest('.comment__body').querySelector('.comment__reply-form');
                    if (replyForm) {
                        replyForm.hidden = !replyForm.hidden;
                        if (!replyForm.hidden) {
                            const textarea = replyForm.querySelector('textarea');
                            if (textarea) textarea.focus();
                        }
                    }
                });
            });
        }
    };

    // =========================================================================
    // Related Articles - Adaptive Grid/Slider (like Reels)
    // =========================================================================
    const RelatedSlider = {
        reduce: false,

        init() {
            this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            document.querySelectorAll('[data-related]').forEach(slider => {
                this.setup(slider);
            });
        },

        setup(slider) {
            const section = slider.closest('.related-sec');
            const track = slider.querySelector('[data-related-track]');
            const nav = section ? section.querySelector('[data-related-nav]') : null;
            const prevBtn = nav ? nav.querySelector('[data-related-prev]') : null;
            const nextBtn = nav ? nav.querySelector('[data-related-next]') : null;

            if (!track) return;

            // Update edge states for CSS masks
            const updateEdges = () => {
                const max = track.scrollWidth - track.clientWidth - 2;
                const x = Math.abs(track.scrollLeft);

                if (prevBtn) prevBtn.disabled = x <= 2;
                if (nextBtn) nextBtn.disabled = x >= max;

                track.classList.toggle('at-start', x <= 2);
                track.classList.toggle('at-end', x >= max);
            };

            // Scroll with throttle
            track.addEventListener('scroll', updateEdges, { passive: true });
            new ResizeObserver(updateEdges).observe(track);
            updateEdges();

            // Navigation buttons - scroll by visible width
            const page = (dir) => {
                track.scrollBy({
                    left: dir * track.clientWidth * 0.8,
                    behavior: this.reduce ? 'auto' : 'smooth'
                });
            };
            if (prevBtn) prevBtn.addEventListener('click', () => page(-1));
            if (nextBtn) nextBtn.addEventListener('click', () => page(1));

            // Show nav if hidden
            if (nav && nav.hidden && track.scrollWidth > track.clientWidth + 10) {
                nav.hidden = false;
            }

            // Drag scrolling (same as Reels)
            this.setupDrag(track, slider);
        },

        setupDrag(track, container) {
            const self = this;
            let drag = null;
            let dragged = false;
            let settle = 0;

            // Find closest snap position
            const snapTo = (x) => {
                const items = track.children;
                const max = track.scrollWidth - track.clientWidth;
                let best = x;
                let dist = Infinity;

                for (const li of items) {
                    const at = Math.min(max, Math.max(0, track.scrollLeft + li.getBoundingClientRect().left - track.getBoundingClientRect().left));
                    if (Math.abs(at - x) < dist) {
                        dist = Math.abs(at - x);
                        best = at;
                    }
                }
                return best;
            };

            const release = () => {
                clearTimeout(settle);
                track.classList.remove('is-dragging');
            };

            track.addEventListener('pointerdown', (e) => {
                if (e.pointerType !== 'mouse' || e.button !== 0) return;
                release();
                drag = { x: e.clientX, left: track.scrollLeft, id: e.pointerId, t: e.timeStamp, v: 0 };
                dragged = false;
            });

            track.addEventListener('pointermove', (e) => {
                if (!drag) return;
                const dx = e.clientX - drag.x;
                if (!dragged && Math.abs(dx) < 6) return;

                if (!dragged) {
                    dragged = true;
                    track.setPointerCapture(drag.id);
                    track.classList.add('is-dragging');
                }

                const dt = e.timeStamp - drag.t;
                if (dt > 0) drag.v = 0.8 * ((track.scrollLeft - (drag.left - dx)) / dt) + 0.2 * drag.v;
                drag.t = e.timeStamp;
                track.scrollLeft = drag.left - dx;
            });

            const endDrag = (e) => {
                if (!drag) return;
                const v = e.timeStamp - drag.t > 90 ? 0 : drag.v;
                drag = null;
                if (!dragged) return;

                const target = snapTo(track.scrollLeft - v * 260);
                track.scrollTo({ left: target, behavior: self.reduce ? 'auto' : 'smooth' });

                const done = () => {
                    if (Math.abs(track.scrollLeft - target) > 2) return;
                    track.removeEventListener('scrollend', done);
                    release();
                };
                track.addEventListener('scrollend', done);
                settle = setTimeout(() => {
                    track.removeEventListener('scrollend', done);
                    release();
                }, 900);
            };

            track.addEventListener('pointerup', endDrag);
            track.addEventListener('pointercancel', endDrag);

            // Prevent click after drag
            track.addEventListener('click', (e) => {
                if (!dragged) return;
                e.preventDefault();
                e.stopPropagation();
                dragged = false;
            }, true);

            // Prevent native drag on links/images
            track.addEventListener('dragstart', (e) => e.preventDefault());
        }
    };

    // =========================================================================
    // Widget Tabs (Most Read/Commented in sidebar)
    // =========================================================================
    const WidgetTabs = {
        init() {
            document.querySelectorAll('.widget.mr .tabs').forEach(tablist => {
                const tabs = tablist.querySelectorAll('[role="tab"]');
                const widget = tablist.closest('.widget');
                const panels = widget.querySelectorAll('[role="tabpanel"]');

                tabs.forEach(tab => {
                    tab.addEventListener('click', () => {
                        // Deactivate all tabs
                        tabs.forEach(t => {
                            t.setAttribute('aria-selected', 'false');
                            t.classList.remove('is-active');
                            t.setAttribute('tabindex', '-1');
                        });

                        // Hide all panels
                        panels.forEach(p => p.hidden = true);

                        // Activate clicked tab
                        tab.setAttribute('aria-selected', 'true');
                        tab.classList.add('is-active');
                        tab.removeAttribute('tabindex');

                        // Show corresponding panel
                        const panelId = tab.getAttribute('aria-controls');
                        const panel = document.getElementById(panelId);
                        if (panel) panel.hidden = false;
                    });
                });
            });
        }
    };

    // =========================================================================
    // Share Buttons (Article page)
    // =========================================================================
    const ShareButtons = {
        init() {
            const self = this;
            document.querySelectorAll('.share__btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const net = btn.dataset.net;
                    const url = encodeURIComponent(window.location.href);
                    const title = encodeURIComponent(document.title);

                    const urls = {
                        facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
                        x: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
                        viber: `viber://forward?text=${title}%20${url}`,
                        whatsapp: `https://wa.me/?text=${title}%20${url}`,
                        telegram: `https://t.me/share/url?url=${url}&text=${title}`,
                        copy: null
                    };

                    if (net === 'copy') {
                        self.copyToClipboard(window.location.href, btn);
                    } else if (urls[net]) {
                        window.open(urls[net], '_blank', 'width=600,height=400');
                    }
                });
            });
        },

        copyToClipboard(text, btn) {
            const showFeedback = () => {
                const icon = btn.querySelector('i');
                if (icon) {
                    const originalClass = icon.className;
                    icon.className = 'fas fa-check';
                    btn.classList.add('is-copied');
                    setTimeout(() => {
                        icon.className = originalClass;
                        btn.classList.remove('is-copied');
                    }, 2000);
                }
            };

            // Try modern Clipboard API first
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text)
                    .then(showFeedback)
                    .catch(() => this.fallbackCopy(text, showFeedback));
            } else {
                this.fallbackCopy(text, showFeedback);
            }
        },

        fallbackCopy(text, callback) {
            // Fallback for older browsers or non-HTTPS
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
                callback();
            } catch (err) {
                console.error('Copy failed:', err);
            }
            document.body.removeChild(textarea);
        }
    };

    // =========================================================================
    // Initialize on DOM Ready
    // =========================================================================
    document.addEventListener('DOMContentLoaded', () => {
        ThemeSwitcher.init();
        DialogManager.init();
        StickyHeader.init();
        CatnavOverflow.init();
        Ticker.init();
        ClubRail.init();
        DropdownNav.init();
        WeatherSlideshow.init();
        Tabs.init();
        StatsAnim.init();
        Chipset.init();
        BlitzTV.init();
        Reels.init();
        ToTop.init();
        Tabbar.init();
        CommentsToggle.init();
        ShareButtons.init();
        WidgetTabs.init();
        RelatedSlider.init();
        // MyTeam.init(); // TODO: Enable when "Моят отбор" feature is ready
    });

})();
